import React, { useState } from 'react';
import { MapPin, AlertCircle, CheckCircle } from 'lucide-react';
import { coreServiceZipCodes } from '../data/deliveryZones';

interface ZipCodeValidatorProps {
  onValidation: (isValid: boolean) => void;
}

interface ValidationResult {
  isValid: boolean;
  message: string;
}

export function ZipCodeValidator({ onValidation }: ZipCodeValidatorProps) {
  const [zipCode, setZipCode] = useState('');
  const [validationResult, setValidationResult] = useState<ValidationResult | null>(null);

  const validateZipCode = (zip: string) => {
    // Check if ZIP code is in service area
    if (zip.length === 5 && coreServiceZipCodes.includes(zip)) {
      const result: ValidationResult = {
        isValid: true,
        message: 'Your location is within our delivery zone.'
      };
      setValidationResult(result);
      onValidation(true);
      return;
    }

    setValidationResult({
      isValid: false,
      message: zip.length === 5 
        ? 'Sorry, this ZIP code is outside our delivery area.'
        : 'Please enter a valid ZIP code.'
    });
    onValidation(false);
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    validateZipCode(zipCode);
  };

  return (
    <div className="bg-white p-6 rounded-lg shadow-sm border border-gray-200">
      <h3 className="text-lg font-semibold text-gray-900 mb-4">Check Delivery Availability</h3>
      
      <form onSubmit={handleSubmit} className="space-y-4">
        <div>
          <label htmlFor="zipCode" className="block text-sm font-medium text-gray-700">
            Enter your ZIP code
          </label>
          <div className="mt-1 relative rounded-md shadow-sm">
            <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
              <MapPin className="h-5 w-5 text-gray-400" />
            </div>
            <input
              type="text"
              id="zipCode"
              name="zipCode"
              value={zipCode}
              onChange={(e) => {
                const value = e.target.value.replace(/\D/g, '').slice(0, 5);
                setZipCode(value);
                if (value.length === 5) {
                  validateZipCode(value);
                }
              }}
              placeholder="Enter ZIP code"
              className="block w-full pl-10 pr-12 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-blue-500 focus:border-blue-500"
              pattern="[0-9]{5}"
              maxLength={5}
              required
            />
          </div>
        </div>

        {validationResult && (
          <div className={`p-4 rounded-md ${
            validationResult.isValid ? 'bg-green-50' : 'bg-red-50'
          }`}>
            <div className="flex">
              <div className="flex-shrink-0">
                {validationResult.isValid ? (
                  <CheckCircle className="h-5 w-5 text-green-400" />
                ) : (
                  <AlertCircle className="h-5 w-5 text-red-400" />
                )}
              </div>
              <div className="ml-3">
                <p className={`text-sm font-medium ${
                  validationResult.isValid ? 'text-green-800' : 'text-red-800'
                }`}>
                  {validationResult.message}
                </p>
              </div>
            </div>
          </div>
        )}
      </form>
    </div>
  );
}